import { useTranslation } from 'react-i18next';

import { buildBoardTheme } from './boardTheme';
import type { QmsDisplayPublic } from './types';
import { useBlinkWindow } from './useBlinkWindow';
import type { DisplayLiveState } from './useDisplayLive';
import { useFullscreen } from './useFullscreen';

/** Single-counter LCD board: one large display code on a themed canvas. */
export function DisplayCounterView({
  board,
  live,
}: {
  board: QmsDisplayPublic;
  live: DisplayLiveState;
}): JSX.Element {
  const { t } = useTranslation('qms');
  const layout = board.layout ?? { kind: 'counter' };
  const theme = buildBoardTheme(layout.accent_color);
  const counter = board.counters[0] ?? null;
  const state = counter ? live.perCounter[counter.id] : undefined;
  const code = state?.currentDisplayCode ?? '—';
  const isActive = state?.status === 'serving';
  const blinking = useBlinkWindow(state?.lastChangeAt);
  const { isFullscreen, toggle } = useFullscreen();

  if (!counter) {
    return (
      <div
        className="flex h-screen items-center justify-center"
        style={{ background: theme.background, color: theme.foreground }}
        data-testid="display-public-fatal"
      >
        <p className="text-2xl" style={{ color: theme.muted }}>
          {t('displays.public.lcd_no_counter')}
        </p>
      </div>
    );
  }

  return (
    <div
      className={`relative flex h-screen items-center justify-center overflow-hidden ${
        blinking ? 'qms-pulse' : ''
      }`}
      data-testid="display-public-canvas"
      data-kind="counter"
      data-counter-id={counter.id}
      style={{ background: theme.background, color: theme.foreground }}
    >
      <button
        type="button"
        onClick={toggle}
        className="absolute end-6 top-6 rounded border px-3 py-1 text-xs uppercase tracking-wider"
        style={{ borderColor: theme.border, color: theme.muted }}
        data-testid="display-fullscreen"
      >
        {t(
          isFullscreen
            ? 'displays.public.exit_fullscreen'
            : 'displays.public.fullscreen',
        )}
      </button>
      <div className="flex flex-col items-center gap-4">
        <p
          className="font-mono font-black leading-none"
          style={{
            color: isActive ? theme.accent : theme.muted,
            fontSize: 'min(60vw, 44rem)',
          }}
          data-testid="display-counter-code"
        >
          {code}
        </p>
        <p
          className="text-xl uppercase tracking-[0.4em]"
          style={{ color: theme.muted }}
        >
          {counter.name}
        </p>
      </div>
    </div>
  );
}
